var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDebounce, useDeepCompareEffect } from 'react-use';
import { HookFormText } from 'components/hookForm';
import { useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useCopy } from 'utils/useCopy';
import { getConversationCustomFieldsFormWarningMessage } from '../../utils/conversationCustomFieldsFormWarningMessage';
import { errorNotification } from '../../utils/notification';
import { useSaveStatusIcon } from '../../utils/useSaveStatusIcon';
const autoSaveDelay = 1000;
/** Renders a hook form text field to display/edit a custom field of type text. */
export const CustomFieldText = memo(function CustomFieldText({ isAcw, customField, save, }) {
    var _a;
    const { attribute, value } = customField;
    const { requiredCustomAttribute } = useFeatureFlag([
        'required-custom-attribute',
    ]);
    const isRequired = requiredCustomAttribute && attribute.is_required;
    const { label, id, description, data_type } = attribute;
    const [copyToClipboard, copyState] = useCopy();
    const notify = useNotifications();
    const [icon, setStatus] = useSaveStatusIcon();
    const savedValue = (_a = value === null || value === void 0 ? void 0 : value.value) !== null && _a !== void 0 ? _a : '';
    const formSavedValues = useMemo(() => ({ [id]: savedValue }), [id, savedValue]);
    const { control, getFieldState, reset, setValue } = useForm({
        mode: 'all',
        defaultValues: formSavedValues,
        shouldFocusError: false,
    });
    const fieldValue = useWatch({ control, name: id });
    useEffect(() => {
        if (isAcw) {
            // A trick to make the field `touched` and `dirty`, so that the warning
            // will be displayed when in wrap-up mode.
            // Using setTimeout because sometimes the field is not already rendered
            setTimeout(() => {
                setValue(id, savedValue, {
                    shouldDirty: true,
                    shouldTouch: true,
                });
            });
        }
    }, [id, isAcw, savedValue, setValue]);
    useDebounce(() => __awaiter(this, void 0, void 0, function* () {
        if (savedValue === fieldValue) {
            return;
        }
        const fieldState = getFieldState(id);
        if (!fieldState.error) {
            const patch = {
                before: (value === null || value === void 0 ? void 0 : value.value)
                    ? {
                        [id]: {
                            data_type,
                            value: value === null || value === void 0 ? void 0 : value.value,
                        },
                    }
                    : {},
                after: fieldValue
                    ? {
                        [id]: {
                            data_type,
                            value: fieldValue,
                        },
                    }
                    : {},
            };
            setStatus('none');
            try {
                yield save(attribute, patch);
                setStatus('success');
            }
            catch (_b) {
                reset(formSavedValues);
                setStatus('error');
                notify(errorNotification);
            }
        }
        else {
            setValue(id, savedValue);
            setStatus('error');
        }
    }), autoSaveDelay, [fieldValue]);
    useDeepCompareEffect(() => {
        reset(formSavedValues);
    }, [formSavedValues]);
    return (_jsx(HookFormText, { control: control, label: label, name: id, helpIcon: {
            text: description,
            dark: true,
        }, rules: isRequired ? { required: true } : {}, meta: {
            warning: getConversationCustomFieldsFormWarningMessage(customField, isAcw, requiredCustomAttribute),
        }, componentProps: {
            icon,
            suffix: {
                type: 'button',
                button: {
                    icon: copyState.status === 'copied' ? 'done' : 'copy',
                    onClick: () => copyToClipboard(fieldValue),
                },
            },
        } }));
});
